/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { PreferencesSlice } from '../../../../ts/state';
import { listAssistantPreferences, setAssistantPreferences } from './actions';
import { entityAdapter } from './adapter';

const initialState = entityAdapter.getInitialState({
  listStatus: ApiStatus.IDLE,
  listFetched: null,
  listError: null,
  setAssistantPreferencesStatus: ApiStatus.IDLE,
  setAssistantPreferencesError: null
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): PreferencesSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// add preferences Slice
const slice = createSlice({
  name: 'preferences',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(listAssistantPreferences.pending, state => {
        state.listStatus = ApiStatus.LOADING;
        state.listError = null;
      })
      .addCase(listAssistantPreferences.fulfilled, (state, action) => {
        state.listStatus = ApiStatus.SUCCEEDED;
        state.listFetched = Date.now();
        state.listError = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(listAssistantPreferences.rejected, (state, action) => {
        state.listStatus = ApiStatus.ERROR;
        state.listError = action.error;
      })
      .addCase(setAssistantPreferences.pending, state => {
        state.setAssistantPreferencesError = null;
        state.setAssistantPreferencesStatus = ApiStatus.LOADING;
      })
      .addCase(setAssistantPreferences.fulfilled, (state, action) => {
        state.setAssistantPreferencesError = null;
        state.setAssistantPreferencesStatus = ApiStatus.SUCCEEDED;

        // If we already have preferences set for this assistant, merge
        // the existing ones with the ones coming from the action payload.
        const payloadAssistantId = action.payload.assistant_id;
        const existingEntity = state.entities[payloadAssistantId];
        const updatedEntity = existingEntity
          ? _.merge(existingEntity, action.payload)
          : action.payload;
        entityAdapter.upsertOne(state, updatedEntity);
      })
      .addCase(setAssistantPreferences.rejected, (state, action) => {
        state.setAssistantPreferencesStatus = ApiStatus.ERROR;
        state.setAssistantPreferencesError = action.error;
      });
  }
});

export default slice.reducer;
